import type { AxiosRequestConfig, ResponseType } from 'axios';

import uefaAxios from '@core/axios';
import EventBusInstance from '@core/eventbus';
import { IS_TEST } from '@utilities/browser';
import { Trace } from '@utilities/logging';
import { MemoryCache } from '@utilities/memory-cache';

export type PollingHeader = {
  key: string;
  value: any;
};

export type PollingOptions = {
  type?: ResponseType;
};

export class Poller {
  url: string;
  headers: PollingHeader[];
  interval: number;
  options: PollingOptions;
  timeout: NodeJS.Timeout;

  constructor(url: string, headers?: PollingHeader[], interval = -1, options?: PollingOptions) {
    if (!Array.isArray(headers)) headers = [];
    this.url = url;
    this.headers = headers;
    this.interval = interval;
    this.options = { ...options };
  }

  handler = null;
  puller = null;
  dataCache = undefined;
  busy = false;

  start() {
    clearTimeout(this.timeout);
    if (this.busy) return;

    this._puller();
  }

  _puller() {
    if (IS_TEST) return;

    // we don't pull if the page is hidden
    if (document.visibilityState === 'hidden') {
      // check again when the page visibility changes
      document.addEventListener('visibilitychange', () => this._puller(), { once: true });
      return;
    }

    const headers: Record<string, string> = {};
    this.headers.forEach(({ key, value }) => {
      if (key && value) headers[key] = value;
    });

    const callOptions: AxiosRequestConfig = {
      responseType: this.options.type,
      headers,
    };

    this.busy = true;
    uefaAxios
      .get(this.url, callOptions)
      .then((resp) => {
        this.dataCache = resp.data ?? this.dataCache;
      })
      .catch((error) => {
        Trace(`url: ${this.url} - error: ${error}`);
      })
      .then(() => {
        EventBusInstance.dispatch(this.url, this.dataCache);

        this.busy = false;
        clearTimeout(this.timeout);

        if (this.interval <= 0) return;
        this.timeout = setTimeout(() => this._puller(), this.interval);
      });
  }

  clear() {
    EventBusInstance.off(this.url);
    clearTimeout(this.timeout);
    this.interval = -1;
  }
}

export class LivePolling {
  pollers = new MemoryCache<string, Poller>();

  on(
    url: string,
    headers: PollingHeader[],
    interval: number,
    handler: (data?: unknown) => unknown,
    context?: any,
    options?: PollingOptions,
  ) {
    EventBusInstance.on(url, handler, context);

    const poller = this.pollers.getOrSet(url, () => new Poller(url, headers, interval, options));

    poller.start(); // we force start the pull (or force a pull)

    if (poller.interval && poller.interval > interval) {
      poller.interval = interval;
    }
  }

  off(url: string, handler: (data?: unknown) => unknown, context?: any) {
    if (!url) {
      throw new Error('url must not be null');
    }
    if (!handler) {
      throw new Error('handler must not be null');
    }

    if (!this.pollers?.size) {
      return false;
    }

    if (EventBusInstance.off(url, handler, context)) return true;

    const poller = this.pollers.get(url);
    if (poller) poller.clear();

    this.pollers.delete(url);

    return this.pollers.size > 0;
  }

  clear() {
    if (!this.pollers) return;

    this.pollers.forEach((poller) => {
      if (typeof poller.clear === 'function') poller.clear();
    });

    this.pollers.clear();
  }

  static readonly instance = new LivePolling();
}

const LivePollingInstance = LivePolling.instance;
export default LivePollingInstance;
